import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RequestData } from 'interfaces/request.interface';
import { fetchRequests, fetchResponses } from 'store/entities/dashboard/dashboardThunk';
import {
  selectRequestsDB,
  selectLastPageDB,
  selectLoadingDB,
  selectPageDB,
  selectPageLimitDB,
  selectPageTotalDB,
  selectResponsesDB,
  selectProcessing,
} from 'store/entities/dashboard/dashboard.selector';
import {
  selectProcessing as selectProcessingRequest,
  selectServiceAction as selectServiceActionRequest,
} from '../../../store/entities/requestdetail/requestdetailSelector';
import { DashboardMainProps, DashboardMainModal, DashboardModalFocus } from './DashboardMain.props';
import { updatePage, resetRequests } from 'store/entities/dashboard/dashboardSlice';

import DashboardMainView from './DashboardMain.view';
import { IResponsesDB } from 'interfaces/response.interface';
import { updateRequestForm } from '../../../store/entities/requests/requestsSlice';
import { RequestFormData } from '../ResourceRequest/hooks/useRequestForm';
import { RequestFormStep } from '../../../common/enums';
import { RequestDetailsModalFocus } from '../RequestDetails/RequestDetails.props';
// import { QueryState } from 'common/enums';

const EmptyModalFocus = {
  modal: DashboardMainModal.UNDEFINED,
  payload: undefined,
};

const DashboardMain: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const responses = useAppSelector(selectResponsesDB);
  const requests = useAppSelector(selectRequestsDB);
  const loading = useAppSelector(selectLoadingDB);
  const isProcessing = useAppSelector(selectProcessing);
  const isRequestProcessing = useAppSelector(selectProcessingRequest);
  const serviceActionRequest = useAppSelector(selectServiceActionRequest);
  const page = useAppSelector(selectPageDB);
  const pagesTotal = useAppSelector(selectPageTotalDB);
  const pageLimit = useAppSelector(selectPageLimitDB);
  const lastPage = useAppSelector(selectLastPageDB);
  const [manageIncidentModal, setManageIncidentModal] = useState<boolean>(false);
  const [offersModal, setOffersModal] = useState<boolean>(false);
  const [modalFocus, setModalFocus] = useState<Partial<DashboardModalFocus>>({
    modal: DashboardMainModal.UNDEFINED,
  });
  const [childModalFocus, setChildModalFocus] = useState<Partial<DashboardModalFocus>>({
    modal: DashboardMainModal.UNDEFINED,
  });


  useEffect(() => {
    const fetchInitialRecord = dispatch(
      fetchRequests({ limit: pageLimit, offset: 0, archived: 0 }),
    );
    return () => fetchInitialRecord.abort();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchInitResponse = dispatch(fetchResponses());
    return () => fetchInitResponse.abort();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchResponseRecord = () => dispatch(fetchResponses());
    const fetchRequestRecord = (num: number) =>
      dispatch(fetchRequests({ limit: pageLimit, offset: num, archived: 0 }));
    const interval = setTimeout(() => {
      if (!loading && !isProcessing) {
        fetchResponseRecord();
        fetchRequestRecord(page);
      }
    }, 12000);

    return () => clearInterval(interval);
  }, [requests, responses]); // eslint-disable-line

  const navigateTo = (path?: string) => {
    navigate([path].filter(Boolean).join('/'));
  };

  const handlePageChange = (num: number) => {
    if (num !== page) {
      dispatch(resetRequests());
      dispatch(fetchRequests({ limit: pageLimit, offset: num, archived: 0 }));
      dispatch(updatePage({ PageNum: num }));
    }
  };

  const handleModalFocus = (modal: Partial<DashboardModalFocus>) => {
    setModalFocus(modal);
  };

  const handleManageIncidentModal = (show: boolean) => {
    //console.log('show', show);
    setManageIncidentModal(show);
  };

  const handleChildModalFocus = (modal: Partial<DashboardModalFocus>) => {
    setChildModalFocus(modal);
  };

  const handleOffersModal = (show: boolean) => {
    //console.log('show', show);
    setOffersModal(show);
  };

  const generateFormValue = (request: RequestData) => {
    const formValue: RequestFormData = {
      step: RequestFormStep.REQUEST_DETAILS,
      requestDetails: {
        incidentId: request.incident.id || '',
        missionType: request.missionType,
        requestNumber: request.requestNumber || 0,
        palmettoResourceRequest: request.palmettoResourceRequest || 0,
        onSceneContactPerson: request.onSceneContactPerson,
        onSceneContactPhone: request.onSceneContactPhone,
        date: request.date,
        departmentName: request.departmentName,
        contactPerson: request.contactPerson,
        contactPhone: request.contactPhone,
        stagingLocation: request.stagingLocation,
        destination: request.destination,
        notes: request.notes,
      },
      resources: {
        ambulance: request.ambulance,
        personnel: request.personnel,
      },
      regions: {
        counties: request.counties,
        serviceType: request.serviceType,
        agencies: request.agencies,
      },
      emailBody: request.emailBody,
    };
    return formValue;
  };

  const onEditRequest = (request: RequestData) => {
    if (request) {
      dispatch(updateRequestForm({ data: generateFormValue(request) }));
      navigate(`/dashboard/request/edit/${request.id}`);
    }
  };

  const onChangeShift = (request: RequestData) => {
    if (request) {
      dispatch(updateRequestForm({ data: generateFormValue(request) }));
      navigate('/dashboard/request/new');
    }
  };

  const combineProps: DashboardMainProps = {
    navigateTo,
    requests,
    responses,
    page,
    pagesTotal,
    lastPage,
    loading,
    isRequestProcessing,
    serviceActionRequest,
    modalFocus,
    childModalFocus,
    handlePageChange,
    handleModalFocus,
    handleChildModalFocus,
    manageIncidentModal,
    handleManageIncidentModal,
    offersModal,
    handleOffersModal,
    onEditRequest,
    onChangeShift,
    resetModalFocus: () => setModalFocus(EmptyModalFocus),
  };
  return <DashboardMainView {...combineProps} />;
};

export default DashboardMain;
